import { HttpContextToken, HttpClient, HttpErrorResponse, HttpContext, HttpParams, HttpHandler } from '@angular/common/http';
import { sha256 } from 'js-sha256';
import { throwError, of, BehaviorSubject, AsyncSubject } from 'rxjs';
import { catchError, map, skip, share, tap } from 'rxjs/operators';

var _a;
/**
 * Basisklasse für den Zugriff auf Services mit tokenbasierter Authentifizierung.
 * Die Annahme ist, dass sich alle Businessmethoden unterhalb einer Basis-URL
 * gruppieren. Diese Basis URL wird an den Konstruktor übergeben. Diese
 * Basisklasse stellt einen HttpClient bereit, der zwei Besonderheiten aufweist:
 *
 * 1. Relative URLs werden gegen die Basis URL aufgelöst und nicht gegen die
 *    URL der beinhaltenden Seite
 * 2. Jeder Request wird (nach login) mit eine "Authorization: Bearer ..."
 *    header versehen, welcher die Basis für die Authentifizierung ist.
 *
 * Für den Login wird angenommen, dass das Backend zwei Endpunkte bereitstellt:
 *
 * <baseUrl>/login/handshake-start
 * <baseUrl>/login/token
 *
 * Der handshake-start Endpunkt wird mit der aktuellen Seite als
 * Rücksprungadresse und einer code_challenge aufgerufen, welche dazu dient den
 * Token Call zu schützen (vgl. PKCE für einen normale OAuth2 handshake).
 *
 * Zum Zeitpunkt des Schreibens dieses Kommentars initiert der Aufruf von
 * login/handshake-start einen OIDC Handshake zwischen Backend und OIDC Server,
 * der dann durch die PKCE-artige Übergabe eines Sessiontokens an das Frontend
 * abgeschlossen wird. Das Sessiontoken selbst kann nur mit dem jeweiligen
 * Backend eingesetzt werden.
 *
 * Zusätzlich zum initialen Handshake wird alle 60 Sekunden der Endpunkt:
 *
 * <baseUrl>/login/check-login
 *
 * aufgerufen, welcher bei aktiven Login die Ablaufzeit des Tokens liefert
 * (ggfs. erweitert das die Laufzeit auch gleich). Mit dem periodischen Call
 * kann das Frontend Backend Logins ggfs. frühzeitig erkennen.
 *
 * Abgeleitete Klassen sollten den HttpClient nutzen, den diese Klasse über das
 * Attribut httpClient bereitstellt.
 *
 * Die Klasse muss in den Startup-Prozess der Anwendung eingebunden sein, da
 * die Methode #appInitialize den Handshake Prozess begleitet. Der Handshake,
 * welcher durch den Call zu login/handshake-start gestartet wird ist eine
 * Sequenz von Redirects, die am Ende wieder zur aufrufenden Seite zurückführen.
 * Die Sequenz wird in einem iframe durchgeführt, da ansonsten CORS
 * Beschränkungen den Zugriff auf das finale Token verhindern. Im iframe
 * extrahiert #appInitialize den Code des Backends und übergibt in per
 * window#postMessage and den aufrufenden Frame, wo er durch #messageHandler
 * verarbeitet wird. Der Rücksprung wird mit dem Parameter
 * loginDone=tudoPassbackV1 signalisiert.
 *
 * Um mehrere Services zu unterstützen wird für jeden Service eine InstanzID
 * generiert, welche in die Rücksprung URL eingebaut wird und damit den
 * richtigen Service erkennbar macht.
 *
 * Neben dem Handshake über einen iframe, kann es notwendig sein, einen
 * "interaktiven" Login durchzuführen. Fälle in denen das relevant wird:
 *
 * - Site des Zielsystems und des Authentifizierungsservers unterscheiden sich
 * - der Service ist nicht mit "implicit Consent" eingerichtet
 *
 * Der interaktive Login wird in einem eigenen Window ausgeführt. Da Popup
 * Blocker ein automatisches Öffnen verhindern wird in solchen Fällen in der
 * Seite ein HTML Overlay erzeugt, welches einen Link zum manuellen Auslösen des
 * Popup enthält. Dieses Verhalten kann per Parameter an den Konstruktor
 * deaktiviert werden.
 *
 * Die SessionID/das Sessiontoken wird im sessionStore gespeichert. Wird ein
 * token vorgefunden, so wird zunächst dieses per login/check-login geprüft und
 * nur wenn keine Session mehr aktiv ist, eine neue etabliert.
 *
 *
 * TL;DR Version zur Nutzung:
 *
 * 1. Nutzende Services erben von dieser Klasse und nutzen deren httpClient
 * 2. #appInitialize muss in die Start-Sequenz eingebunden sein. Bspw.:
 *   @NgModule({
 *   providers: [
 *    {
 *     provide: APP_INITIALIZER,
 *     useFactory: (instance: DemoService) => () => instance.appInitialize(),
 *     multi: true,
 *     deps: [DemoService]
 *    }
 *   ]
 *   })
 * 3. Der Service muss an allen Nutzungsstellen die gleiche Instanz sein, d.h.
 *    er sollte
 *
 *    @Injectable({
 *      providedIn: 'root',
 *    })
 *
 *    markiert sein.
 */
class LoginHandlingSevice {
    static { this.SKIP_TOKEN_HANDLING = new HttpContextToken(() => false); }
    #instanceId;
    #baseUrl;
    #sessionStoreKey;
    #verifier;
    #interactiveVerifier;
    /**
     * @type HTMLIFrameElement
     */
    #iframe;
    #iframeWatchdogId;
    /**
     * @type WindowProxy
     */
    #windowRef;
    #loginPromiseSource;
    #loginPromise;
    #token;
    #expires;
    #expiresMonitorId;
    #loginStateSubscription;
    #connectionMonitorId;
    #connectionMonitor;
    #httpClient;
    /**
     * The handshake for fetching the token is done in an iframe or window
     * this method must be called as one of the first methods so that it can
     * check if it is called from the IFrame/window. If that is the case it
     * will extract the data (either error data or authentication code) and
     * pass it to the outer context using window#postMessage
     *
     * This is intended to be called by the angular APP_INITIALIZER. This method
     * will either return a completed Observable or an observable throwing an
     * error to stop angular initialization. The error declares that it is
     * intentional, so this seems ok.
     */
    appInitialize() {
        const origin = window.location.origin;
        const params = new URL(document.location.href).searchParams;
        // Handle "return case". Return as in the final redirect from the resource
        // server
        if (params.get('loginDone') === 'tudoPassbackV1') {
            const target = params.get('interactive') === 'true' ? window.opener : window.parent;
            const message = {
                protocol: 'tudoPassbackV1',
                instanceId: params.get('instanceId'),
                interactive: params.get('interactive') === 'true'
            };
            if (params.get('error')) {
                message.error = {
                    description: params.get('error_description'),
                    code: params.get('error')
                };
            }
            else {
                message.code = params.get('code');
            }
            target.postMessage(message, origin);
            // Stop angular initialization - logs error in console, but that
            // seems to be an acceptable drawback
            return throwError(() => 'Initialization stopped - only handling callback - this is intentional!');
        }
        else {
            return of(true);
        }
    }
    constructor(baseUrl, httpHandler) {
        /**
         * BehaviorSubject to monitor the loginState of the service
         */
        this.loginState = new BehaviorSubject(false);
        /**
         * BehaviorSubject to monitor login errors
         */
        this.loginError = new BehaviorSubject(null);
        this.#instanceId = _a.#createRandomString(20);
        this.#connectionMonitor = () => this.checkLogin().subscribe();
        this.#httpClient = new HttpClient(new BearerTokenHandler(httpHandler, baseUrl, () => this.#token, () => this.#logout(false)));
        this.#baseUrl = baseUrl;
        this.#sessionStoreKey = 'tudo.login-handling-service.token.' + this.#baseUrl;
        window.addEventListener('message', (e) => this.#messageHandler(e));
        this.#httpClient.get('', {});
    }
    /**
     * Get read-only access to the baseUrl this service was created with
     */
    get baseUrl() {
        return this.#baseUrl;
    }
    /**
     * Get read-only access to the current authentication token
     */
    get token() {
        return this.#token;
    }
    /**
     * Get read-only access to the expires timestamp for the current token
     */
    get expires() {
        return this.#expires;
    }
    get httpClient() {
        return this.#httpClient;
    }
    /**
     * Check if there is a token present and if it is still valid based on
     * its expiry timestamp
     */
    isLoggedIn() {
        return (!!this.#token) && (!!this.#expires) && this.#expires.getTime() > new Date().getTime();
    }
    /**
     * Check if the login is still active using a backend call
     * (Endpoint <baseUrl>/login/check-login). On successful completion the
     * expires timestamp will be update to the current value
     */
    checkLogin() {
        return this.#httpClient
            .get('login/check-login')
            .pipe(catchError(err => {
            if (err instanceof HttpErrorResponse && err.status === 401) {
                return of(false);
            }
            else {
                return throwError(() => err);
            }
        }), map(resp => {
            if (resp) {
                this.#storeToken(this.#token, resp.expires);
                return true;
            }
            else {
                return false;
            }
        }));
    }
    /**
     * Initiate the login
     */
    login() {
        if (this.#loginPromise) {
            return this.#loginPromise;
        }
        if (!this.#loginStateSubscription) {
            this.#loginStateSubscription = this.loginState
                .pipe(skip(1))
                .subscribe({
                next: (newState) => this.#loginStateChanged(newState)
            });
        }
        this.#verifier = undefined;
        this.#loginPromiseSource = new AsyncSubject();
        this.#loginPromise = this.#loginPromiseSource
            .pipe(catchError((e) => {
            console.log('Login failed', e);
            this.loginError.next(e);
            return throwError(() => e);
        }), share());
        const storedToken = sessionStorage.getItem(this.#sessionStoreKey);
        this.httpClient
            .get('login/check-login', {
            headers: { 'Authorization': 'Bearer ' + storedToken },
            context: new HttpContext().set(_a.SKIP_TOKEN_HANDLING, true)
        })
            .subscribe({
            next: (res) => {
                this.#storeToken(storedToken == null ? undefined : storedToken, res.expires);
            },
            error: () => {
                const loginUrl = this.#buildLoginUri(false);
                const loginProtocol = new URL(loginUrl).protocol;
                if (loginProtocol !== window.location.protocol) {
                    window.setTimeout(() => {
                        const message = {
                            protocol: 'tudoPassbackV1',
                            instanceId: this.#instanceId,
                            error: {
                                code: 'interaction_required',
                                description: 'Backend protocol does not match frontend protocol',
                                interactive: false
                            }
                        };
                        window.postMessage(message, origin);
                    });
                }
                else {
                    this.#iframe = document.createElement('IFRAME');
                    this.#iframe.style.display = 'none';
                    this.#iframe.src = loginUrl;
                    const body = document.getElementsByTagName('body')[0];
                    body.appendChild(this.#iframe);
                    this.#iframeWatchdogId = window.setTimeout(() => {
                        const message = {
                            protocol: 'tudoPassbackV1',
                            instanceId: this.#instanceId,
                            error: {
                                code: 'iframe_timeout',
                                description: 'Login did not succeed in 20s',
                                interactive: false
                            }
                        };
                        window.postMessage(message, origin);
                    }, 20000);
                }
            }
        });
        return this.#loginPromise;
    }
    /**
     * Companion method to handleLoginCallback
     */
    #messageHandler(e) {
        if (e.origin === window.location.origin) {
            if (e?.data?.instanceId === this.#instanceId && (this.#verifier || this.#interactiveVerifier)) {
                window.clearTimeout(this.#iframeWatchdogId);
                this.#iframeWatchdogId = undefined;
                if (this.#iframe) {
                    this.#iframe.remove();
                    this.#iframe = undefined;
                }
                if (this.#windowRef) {
                    this.#windowRef.close();
                    this.#windowRef = undefined;
                }
                let verifier;
                if (e?.data?.interactive) {
                    verifier = this.#interactiveVerifier;
                    this.#interactiveVerifier = undefined;
                }
                else {
                    verifier = this.#verifier;
                    this.#verifier = undefined;
                }
                if (e?.data?.code && verifier) {
                    const form = new HttpParams()
                        .set('code', e.data.code)
                        .set('code_verifier', verifier);
                    this.#httpClient.post('login/token', form)
                        .subscribe({
                        next: t => {
                            this.#storeToken(t.token, t.expires);
                        },
                        error: e2 => {
                            this.#storeToken(undefined, undefined);
                            const asyncSubject = this.#loginPromiseSource;
                            this.#loginPromiseSource = undefined;
                            this.#loginPromise = undefined;
                            if (asyncSubject) {
                                asyncSubject.error(e2);
                                asyncSubject.complete();
                            }
                        }
                    });
                }
                else {
                    let loginError;
                    if (e.data.error.code) {
                        loginError = new LoginError(e.data.error.code, e.data.error.description);
                    }
                    else {
                        loginError = new LoginError('unknown_error', JSON.stringify(e.data));
                    }
                    const asyncSubject = this.#loginPromiseSource;
                    this.#loginPromiseSource = undefined;
                    this.#loginPromise = undefined;
                    if (asyncSubject) {
                        asyncSubject.error(loginError);
                        asyncSubject.complete();
                    }
                }
            }
        }
    }
    #storeToken(token, expiresMillis, skipPromiseHandling = false) {
        this.#token = token;
        this.#expires = expiresMillis ? new Date(new Date().getTime() + expiresMillis - 500) : undefined;
        if (this.#expiresMonitorId) {
            window.clearTimeout(this.#expiresMonitorId);
            this.#expiresMonitorId = undefined;
        }
        if (this.#expires) {
            // Register a timeout handler that fires _after_ expires is reached.
            // In normal operation it should not happen as the keep alive will kick
            // in before this time
            this.#expiresMonitorId = window.setTimeout(() => this.loginState.next(this.isLoggedIn()), this.#expires.getTime() - new Date().getTime() + 50);
        }
        if (!skipPromiseHandling) {
            const asyncSubject = this.#loginPromiseSource;
            this.#loginPromiseSource = undefined;
            this.#loginPromise = undefined;
            if (asyncSubject) {
                asyncSubject.next(null);
                asyncSubject.complete();
            }
        }
        this.loginState.next(this.isLoggedIn());
        if (!this.#connectionMonitorId) {
            this.#connectionMonitorId = window.setInterval(this.#connectionMonitor, 60000);
        }
        if (this.#token) {
            sessionStorage.setItem(this.#sessionStoreKey, this.#token);
        }
        else {
            sessionStorage.removeItem(this.#sessionStoreKey);
        }
    }
    /**
     * For an interactive login (where the user can login, give consent, or
     * where cross-site access to cookies is possible) a window is used.
     */
    interactiveLogin() {
        const windowRef = window.open(this.#buildLoginUri(true), 'loginWindow' + this.#instanceId);
        this.#windowRef = windowRef ? windowRef : undefined;
    }
    /**
     * Disconnect from the backend service
     */
    logout() {
        this.#logout(true);
    }
    /**
     * Disconnect from the backend service
     */
    #logout(fromUser) {
        if (fromUser) {
            if (this.#loginStateSubscription) {
                this.#loginStateSubscription.unsubscribe();
                this.#loginStateSubscription = undefined;
            }
            if (this.#connectionMonitorId) {
                window.clearInterval(this.#connectionMonitorId);
                this.#connectionMonitorId = undefined;
            }
        }
        // only touch login handling promise if the logout request was a manual
        // logout - for automatic logouts (401) a login might already be running and
        // must not be intercepted
        this.#storeToken(undefined, undefined, !fromUser);
    }
    /**
     * Build the login URL for the start-handshake endpoint
     */
    #buildLoginUri(interactive) {
        let codeChallenge;
        let codeChallengeMethod;
        let verifier;
        if (interactive) {
            if (!this.#interactiveVerifier) {
                this.#interactiveVerifier = _a.#createRandomString(50);
            }
            verifier = this.#interactiveVerifier;
        }
        else {
            if (!this.#verifier) {
                this.#verifier = _a.#createRandomString(50);
            }
            verifier = this.#verifier;
        }
        if ((typeof sha256) === 'function') {
            codeChallengeMethod = 'S256';
            const sha256Array = sha256.array(verifier);
            const sha256ArrayBase64 = btoa(String.fromCharCode(...sha256Array))
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=+$/, '');
            codeChallenge = sha256ArrayBase64;
        }
        else {
            codeChallengeMethod = 'plain';
            codeChallenge = verifier;
        }
        const url = new URL(this.#baseUrl + 'login/handshake-start');
        if (interactive) {
            url.searchParams.set('interactive', 'true');
        }
        const returnUrl = new URL(window.location.href);
        returnUrl.hash = '';
        returnUrl.search = '';
        returnUrl.searchParams.set('loginDone', 'tudoPassbackV1');
        returnUrl.searchParams.set('interactive', interactive ? 'true' : 'false');
        returnUrl.searchParams.set('instanceId', this.#instanceId);
        url.searchParams.set('code_challenge_method', codeChallengeMethod);
        url.searchParams.set('code_challenge', codeChallenge);
        url.searchParams.set('redirect_uri', returnUrl.toString());
        return url.toString();
    }
    /**
     * Dieser Listener wird immer aufgerufen, wenn ein 401 erzeugt wird. In diesem
     * Fall wird ein erneuter Login durchgeführt. Wenn periodisch der Login Status
     * überprüft wird, wird hier auch ein Reconnect ausgelöst, falls die Session
     * zwischenzeitlich verloren geht.
     */
    #loginStateChanged(newState) {
        if (!newState) {
            this.login().subscribe({
                next: () => { },
                error: (e) => {
                    console.log('#connectionStateChanged, Login failed', e);
                    this.loginError.next(e);
                }
            });
        }
        else {
            this.loginError.next(null);
        }
    }
    static #createRandomString(length) {
        let result = '';
        const candiates = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        for (let i = 0; i < length; i++) {
            result += candiates.charAt(Math.floor(Math.random() * candiates.length));
        }
        return result;
    }
}
_a = LoginHandlingSevice;
/**
 * Wrapper class to report login errors as for example thrown from OAuth2
 * Handshake (like code "interaction_required", which indicates, that
 * authentication without user intervention was requested, but failed.
 */
class LoginError {
    constructor(code, description) {
        this.code = code;
        this.description = description;
    }
}
class BearerTokenHandler extends HttpHandler {
    #backend;
    #baseUrl;
    #tokenProvider;
    #authenticationErrorHandler;
    constructor(backend, baseUrl, tokenProvider, authenticationErrorHandler) {
        super();
        this.#baseUrl = new URL(baseUrl);
        this.#backend = backend;
        this.#tokenProvider = tokenProvider;
        this.#authenticationErrorHandler = authenticationErrorHandler;
    }
    handle(req) {
        const token = this.#tokenProvider();
        const skipTokenHandling = req.context.get(LoginHandlingSevice.SKIP_TOKEN_HANDLING) === true;
        if (token && !skipTokenHandling) {
            // Inject bearer token
            req = req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + this.#tokenProvider()),
                url: new URL(req.url, this.#baseUrl).toString()
            });
        }
        else {
            req = req.clone({
                url: new URL(req.url, this.#baseUrl).toString()
            });
        }
        return this.#backend
            .handle(req)
            .pipe(tap({
            error: err => {
                if (err instanceof HttpErrorResponse && err.status === 401 && !skipTokenHandling) {
                    // if we get a 401 and the token is still current (was not renewed
                    // inbetween), invoke the authenticationErrorHandler
                    if (token === this.#tokenProvider()) {
                        this.#authenticationErrorHandler(err);
                    }
                }
            }
        }));
    }
}

/*
 * Public API Surface of ngx-oidcclient
 */

/**
 * Generated bundle index. Do not edit.
 */

export { LoginError, LoginHandlingSevice };
